import React, { useState } from 'react';

import { MenuItem } from '@mui/material';
import Divider from '@mui/material/Divider';
import { IconMenuItem } from 'mui-nested-menu';

import { PoapViewResponse } from 'api/queries/getPoapPrimaryViews.query';

import { ConfirmDeleteModal } from 'components/common/ConfirmDeleteModal/ConfirmDeleteModal';
import { ConfirmSaveModal } from 'components/common/ConfirmSaveModal/ConfirmSaveModal';
import { TooltipPosition } from 'components/common/CustomizedTooltip/customizedTooltip.enums';
import { MuiIcon } from 'components/common/Icons/base/MuiIcon/MuiIcon';

import { Button } from 'components/DesignSystem/Button';
import { Switch } from 'components/DesignSystem/Switch';
import { CustomizedDropdownMenu, CustomizedTooltip } from 'components/common';
import { RenameViewModal } from 'components/pages/ScheduleViewsPage/ViewsManager/components/RenameViewModal/RenameViewModal';

import classes from './LabelWithMenu.module.scss';

interface Props {
    value: string;
    onDelete: (viewId: string) => Promise<void>;
    onRenameView: (viewId: string, newName: string) => Promise<void>;
    onDuplicateView: (viewId: string, currentViewName: string) => Promise<void>;
    onSetVisibility: (viewId: string, hide: boolean) => void;
    onSetPrivacyMode: (viewId: string, isPublic: boolean) => void;
    isLoadingDelete: boolean;
    isLoadingDuplicateView?: boolean;
    isLoadingSetVisibility?: boolean;
    isLoadingSetPrivacyMode?: boolean;
    view: PoapViewResponse;
}
const MENU_FONT_SIZE = '13px';
const MENU_ICON_SIZE = '17px';

export const LabelWithMenu = ({
    value,
    onDelete,
    onRenameView,
    onDuplicateView,
    onSetVisibility,
    onSetPrivacyMode,
    isLoadingDelete,
    isLoadingDuplicateView = false,
    isLoadingSetVisibility = false,
    isLoadingSetPrivacyMode = false,
    view,
}: Props) => {
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState<boolean>(false);
    const [isHideModalOpen, setIsHideModalOpen] = useState<boolean>(false);
    const [isRenameModalOpen, setIsRenameModalOpen] = useState<boolean>(false);
    const [isPrivacyModalOpen, setIsPrivacyModalOpen] = useState<boolean>(false);

    return (
        <div className={classes.TabLabel}>
            <span>{view.viewName}</span>
            <CustomizedDropdownMenu
                menuStyles={{ maxHeight: 'initial' }}
                triggerElement={
                    <Button variant={'tertiary'} size={'small'} className={classes.threeDotButtonOverride}>
                        <MuiIcon icon={'more_vert'} />
                    </Button>
                }
            >
                {!view.isOwner && (
                    <>
                        <MenuItem disabled={true} sx={{ opacity: '1 !important' }}>
                            <IconMenuItem
                                sx={{
                                    '& .MuiTypography-root': {
                                        fontSize: MENU_FONT_SIZE,
                                        position: 'relative',
                                    },
                                    '& .MuiBox-root': {
                                        alignItems: 'center',
                                    },
                                }}
                                leftIcon={<MuiIcon icon={'person'} fontSize={'18px'} />}
                                onClick={() => setIsPrivacyModalOpen(true)}
                                label={
                                    <div className={classes.ownedByWrapper}>
                                        <div className={classes.title}>Owned by:</div>
                                        <div className={classes.value}>{view.viewOwner}</div>
                                    </div>
                                }
                            />
                        </MenuItem>
                        <Divider />
                    </>
                )}

                <CustomizedTooltip
                    tooltipPosition={TooltipPosition.Right}
                    disabled={view.isOwner}
                    tooltipContent={"You can't change privacy of this view because you don't own it"}
                    triggerElement={
                        <MenuItem disabled={!view.isOwner}>
                            <IconMenuItem
                                sx={{
                                    '& .MuiTypography-root': {
                                        fontSize: MENU_FONT_SIZE,
                                        position: 'relative',
                                    },
                                    '& .MuiBox-root': {
                                        alignItems: 'center',
                                    },
                                }}
                                leftIcon={<MuiIcon icon={'public'} fontSize={'18px'} />}
                                rightIcon={
                                    <Switch
                                        checked={view.public}
                                        disabled={isLoadingSetPrivacyMode}
                                        onChange={() => setIsPrivacyModalOpen(true)}
                                        className={classes.switchOverride}
                                        onClick={(e: React.MouseEvent) => e.stopPropagation()}
                                    />
                                }
                                onClick={() => setIsPrivacyModalOpen(true)}
                                label={`Make view ${view.public ? 'private' : 'public'}`}
                            />
                        </MenuItem>
                    }
                />

                <CustomizedTooltip
                    tooltipPosition={TooltipPosition.Right}
                    disabled={view.isOwner}
                    tooltipContent={"You can't rename this view because you don't own it"}
                    triggerElement={
                        <MenuItem disabled={!view.isOwner}>
                            <IconMenuItem
                                sx={{
                                    '& .MuiTypography-root': {
                                        fontSize: MENU_FONT_SIZE,
                                        position: 'relative',
                                    },
                                    '& .MuiBox-root': {
                                        alignItems: 'center',
                                    },
                                }}
                                leftIcon={<MuiIcon icon={'edit'} type={'outlined'} fontSize={MENU_ICON_SIZE} />}
                                onClick={() => setIsRenameModalOpen(true)}
                                label={'Rename view'}
                            />
                        </MenuItem>
                    }
                />

                <MenuItem disabled={isLoadingDuplicateView}>
                    <IconMenuItem
                        sx={{
                            '& .MuiTypography-root': {
                                fontSize: MENU_FONT_SIZE,
                                position: 'relative',
                            },
                            '& .MuiBox-root': {
                                alignItems: 'center',
                            },
                        }}
                        leftIcon={<MuiIcon icon={'content_copy'} type={'outlined'} fontSize={MENU_ICON_SIZE} />}
                        onClick={() => {
                            onDuplicateView(value, view.viewName);
                        }}
                        label={'Duplicate view'}
                    />
                </MenuItem>

                <MenuItem>
                    <IconMenuItem
                        sx={{
                            '& .MuiTypography-root': {
                                fontSize: MENU_FONT_SIZE,
                                position: 'relative',
                            },
                            '& .MuiBox-root': {
                                alignItems: 'center',
                            },
                        }}
                        leftIcon={<MuiIcon icon={'visibility_off'} type={'outlined'} fontSize={MENU_ICON_SIZE} />}
                        onClick={() => setIsHideModalOpen(true)}
                        label={'Hide view'}
                    />
                </MenuItem>

                {view.isOwner && <Divider />}
                <CustomizedTooltip
                    tooltipPosition={TooltipPosition.Right}
                    disabled={view.isOwner}
                    tooltipContent={"You can't delete this view because you don't own it"}
                    triggerElement={
                        <MenuItem disabled={!view.isOwner}>
                            <IconMenuItem
                                sx={{
                                    '& .MuiTypography-root': {
                                        fontSize: MENU_FONT_SIZE,
                                        position: 'relative',
                                        color: `var(--menu-destructive-fg)`,
                                        top: '1px',
                                    },
                                    '& .MuiBox-root': {
                                        alignItems: 'center',
                                    },
                                }}
                                leftIcon={
                                    <MuiIcon
                                        icon={'delete'}
                                        type={'outlined'}
                                        fontSize={MENU_ICON_SIZE}
                                        color={`var(--menu-destructive-fg)`}
                                    />
                                }
                                onClick={() => setIsDeleteModalOpen(true)}
                                label={'Delete view'}
                            />
                        </MenuItem>
                    }
                />
            </CustomizedDropdownMenu>
            <ConfirmDeleteModal
                open={isDeleteModalOpen}
                onClose={() => setIsDeleteModalOpen(false)}
                action={() => onDelete(value)}
                title={'Delete view'}
                isLoading={isLoadingDelete}
            >
                {`Do you want to delete ${view.viewName}?`}
            </ConfirmDeleteModal>
            <ConfirmSaveModal
                isShowModal={isHideModalOpen}
                onHideModal={() => setIsHideModalOpen(false)}
                onConfirm={() => {
                    try {
                        onSetVisibility(value, true);
                        setIsHideModalOpen(false);
                    } catch {
                        // Error handled in logic; keep modal open
                    }
                }}
                isLoading={isLoadingSetVisibility}
                actionButtonLabel={'Hide'}
            >
                Are you sure you want to hide the view? It will be stored in additional views.
            </ConfirmSaveModal>
            <ConfirmSaveModal
                title={view.public ? 'Unpublish view' : 'Publish view'}
                isShowModal={isPrivacyModalOpen}
                onHideModal={() => setIsPrivacyModalOpen(false)}
                onConfirm={() => {
                    try {
                        onSetPrivacyMode(value, !view.public);
                        setIsPrivacyModalOpen(false);
                    } catch {
                        // Error handled in logic; keep modal open
                    }
                }}
                isLoading={isLoadingSetPrivacyMode}
                actionButtonLabel={view.public ? 'Unpublish' : 'Publish'}
            >
                {view.public
                    ? 'Are you sure you want to make this view private? It will no longer be visible to other users.'
                    : 'Are you sure you want to make this view public? It will be visible to all users with access to this schedule.'}
            </ConfirmSaveModal>
            <RenameViewModal
                isModalOpen={isRenameModalOpen}
                setIsModalOpen={setIsRenameModalOpen}
                initialName={view.viewName}
                onSubmit={async (newName) => {
                    await onRenameView(value, newName);
                }}
            />
        </div>
    );
};
